import moment, { Moment } from "moment";
import { useEffect, useState } from "react";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { visuallyHidden } from "@mui/utils";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import {
  Box,
  Chip,
  Container,
  TableContainer,
  TablePagination,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import InfoChip from "components/utils/InfoChip";
import ExportCSV from "components/user/ExportCSV";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import LinearIndeterminate from "components/utils/LinearLoader";
import { Order, PaginationSchema } from "hooks/pagination";
import { UserAuditLog, useUserServiceAuditLogs } from "hooks/user/audit";

// const Row = (props: { row: ReturnType<typeof createData> }) => {
const EventRow = ({ log }: { log: UserAuditLog }) => {
  const { email, updatedAt, status, type, project, host, message, url } = log;

  const Event = (
    <Chip size="small" color="primary" label={type.replaceAll("_", " ")} />
  );

  const Status =
    status === "success" || status === "canceled" ? (
      <Chip size="small" color="success" variant="filled" label={status} />
    ) : (
      <Chip size="small" color="error" variant="filled" label={status} />
    );

  const infoChip = !!message ? (
    <InfoChip info={message} size="small" placement="left" />
  ) : (
    "--"
  );

  return (
    <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
      <TableCell component="th" scope="row">
        <b>{email}</b>
      </TableCell>
      <TableCell align="right">
        {new Date(updatedAt).toLocaleString() ?? "--"}
      </TableCell>
      <TableCell align="right">{Status}</TableCell>
      <TableCell align="right">
        <Tooltip title={url} placement="right">
          {Event}
        </Tooltip>
      </TableCell>
      <TableCell align="right">{project ?? "--"}</TableCell>
      <TableCell align="right">{host ?? "--"}</TableCell>
      <TableCell align="right" sx={{ paddingY: 0 }}>
        {infoChip}
      </TableCell>
    </TableRow>
  );
};

type DateSchema = {
  updatedAt?: {
    gte: string;
    lt: string;
  };
};

export const Audit = () => {
  const [page, setPage] = useState<number>(0);
  const [sort, setSort] = useState<string>("updatedAt");
  const [order, setOrder] = useState<Order>("desc");
  const [limit, setLimit] = useState<number>(10);

  // states to manage date range filters (from -> to)
  const [dateLt, setDateLt] = useState<Moment | null>(null);
  const [dateGte, setDateGte] = useState<Moment | null>(null);

  const [body, setBody] = useState<PaginationSchema & DateSchema>({
    page,
    sort,
    order,
    limit,
  });

  // frontend/memory search
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredLogs, setFilteredLogs] = useState<UserAuditLog[]>();

  const logs = useUserServiceAuditLogs(body);

  const handleSort = (event: React.MouseEvent<unknown>, key: string) => {
    const isAsc = sort === key && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setSort(key);
    setOrder(newOrder);
  };

  const handleChangePage = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeLimit = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    // filter results by search term; scans user, type, & project (if applicable) fields
    if (!!logs.data) {
      const term = searchTerm.toLowerCase();
      const results = logs.data.data.filter(
        ({ email, type, project }) =>
          email.toLowerCase().includes(term) ||
          type.toLowerCase().includes(term) ||
          (project && project.toLowerCase().includes(term))
      );

      setFilteredLogs(results);
    }
  }, [searchTerm, logs.data]);

  useEffect(() => {
    // if date range is set, apply to post body filter
    if (!!dateGte && !!dateLt) {
      setBody({
        ...body,
        updatedAt: {
          gte: dateGte.toISOString(),
          lt: dateLt.toISOString(),
        },
      });
    }
  }, [dateGte, dateLt]);

  useEffect(() => {
    setBody({ ...body, page, sort, order, limit });
  }, [page, sort, order, limit]);

  useEffect(() => {
    document.title = "GRACE | audit";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Audit"
          icon={<PendingActionsIcon />}
          buttons={[
            <TextField
              key={"audit-search-term"}
              type="text"
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mr: 2 }}
            />,
            <TextField
              key={"audit-date-filter-gte"}
              id="date-filter-gte"
              size="small"
              label="From"
              type="datetime-local"
              value={dateGte?.format("YYYY-MM-DDTHH:mm")}
              onChange={(e: any) => setDateGte(moment(e.target.value))}
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 2 }}
            />,
            <TextField
              key={"audit-date-filter-lt"}
              id="date-filter-lt"
              size="small"
              label="To"
              type="datetime-local"
              value={dateLt?.format("YYYY-MM-DDTHH:mm")}
              onChange={(e: any) => setDateLt(moment(e.target.value))}
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 2 }}
            />,
            <ExportCSV
              key={"audit-csv-export"}
              data={filteredLogs}
              headers={conf.COLUMNS.AUDIT_CSV}
              fileName="GRACE_AUDIT_LOGS"
            />,
            <TablePagination
              key={"audit-pagination"}
              component="div"
              page={page}
              count={logs.data?.meta?.total || 0}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />,
            <Refresh
              key={"audit-refresh"}
              isFetching={logs.isFetching}
              onClick={logs.refetch}
            />,
          ]}
        />

        <TableContainer sx={{ boxShadow: 4, maxHeight: 630 }}>
          <Table aria-label="collapsible table" stickyHeader>
            <TableHead>
              <TableRow>
                {conf.COLUMNS.AUDIT.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.isHeader ? "left" : "right"}
                    sortDirection={order}
                  >
                    <TableSortLabel
                      active={sort === column.key}
                      direction={sort === column.key ? order : "asc"}
                      onClick={(event: React.MouseEvent<unknown>) =>
                        handleSort(event, column.key)
                      }
                    >
                      <b>{column.label}</b>
                      {sort === column.key ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align={"right"}>
                  <b>Note</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {logs.isLoading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearIndeterminate />
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs?.map((log: UserAuditLog) => (
                  <EventRow key={log.id} log={log} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </ProtectedRoute>
  );
};
